import {
  MAX_PAGE_SIZE,
  type RolesListQuerySchema,
  type Paginated,
  type Role,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';
import { type SelectionOption } from '@/shared/components/selection/types/selection-option.types';
import {
  type ServerSelectQueryArgs,
  type UseServerSelectQuery,
  type UseServerSelectResolve,
} from '@/shared/components/selection/types/server-selection.types';

/**
 * The query as it goes ON THE WIRE — the schema's INPUT, not its output.
 *
 * The difference is `ids`: the contract parses one comma-joined parameter into
 * an array, so the parsed type is `string[]` while the thing to send is
 * `'a,b,c'`. Typing the request by the output would make RTK Query serialize
 * `?ids=a&ids=b`, which `qs` hands the api as an array — exactly the ambiguity
 * `IdsParamSchema` exists to remove (N7).
 */
type RoleLookupParams = z.input<typeof RolesListQuerySchema>;

/**
 * The **cross-module read bridge** for roles (W6).
 *
 * Any surface outside `modules/Admins` that needs to name a role — a staff-user
 * filter, an audit row, a notification segment — reads it through here rather
 * than importing `modules/Admins/services/roles.api`. That import is banned in
 * both directions: `shared/` may never reach into a module (W2), and one module
 * may never reach into another's api (W6). Without a bridge, the first surface
 * that needs a role name quietly couples two modules together, and every module
 * after it follows.
 *
 * It is read-only on purpose. Writing a role is the owning module's business,
 * and a bridge that could mutate would be a second write path around the events
 * the owner publishes (W5).
 */
export const roleLookupApi = createApi({
  reducerPath: 'roleLookupApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['RoleLookup'],
  endpoints: (builder) => ({
    lookupRoles: builder.query<Paginated<Role>, RoleLookupParams>({
      query: (params) => ({ url: '/admin/roles', method: 'GET', params }),
      providesTags: ['RoleLookup'],
    }),
  }),
});

export const { useLookupRolesQuery } = roleLookupApi;

function toItem(role: Role): SelectionOption {
  return {
    value: role.id,
    label: role.name,
    description: role.description ?? undefined,
  };
}

const EMPTY: readonly SelectionOption[] = [];

/** Adapts the bridge to `ServerSelect`'s option-page contract. */
export const useRoleOptionsQuery: UseServerSelectQuery = ({
  search,
  page,
  pageSize,
}: ServerSelectQueryArgs) => {
  const { data, isFetching, isError, refetch } = useLookupRolesQuery({
    page,
    pageSize,
    order: 'asc',
    ...(search === '' ? {} : { search }),
  });

  return {
    items: data === undefined ? EMPTY : data.items.map(toItem),
    hasNext: data?.meta.hasNext ?? false,
    isFetching,
    isError,
    // So a failed load offers a way out rather than a dead-end message (U0).
    refetch,
  };
};

/**
 * Resolves saved role ids that may be on no page at all — the `ids` filter added
 * to `RolesListQuerySchema` in this phase.
 *
 * This is the whole reason that filter exists: a bookmarked `?roleKey=…` or a
 * value saved last month must render its NAME, and the alternatives were an N+1
 * of detail calls or the `pageSize=100` preload W6 forbids.
 */
export const useResolveRoleIds: UseServerSelectResolve = (ids: readonly string[]) => {
  const { data, isFetching } = useLookupRolesQuery(
    {
      page: 1,
      // Clamped, because the api REFUSES an over-large page rather than clamping
      // it (N7): a form holding more saved ids than one page can carry would 422
      // every resolve and name none of them. `useServerOptions` slices to the
      // same ceiling; this is the bridge's own half of the guard.
      pageSize: Math.min(Math.max(ids.length, 1), MAX_PAGE_SIZE),
      order: 'asc',
      ids: ids.join(','),
    },
    // Nothing to resolve is not an empty request — it is no request.
    { skip: ids.length === 0 },
  );

  return {
    items: data === undefined ? EMPTY : data.items.map(toItem),
    isFetching,
  };
};
